/**
 * 客户端任务服务：内存缓存(按自然日)、客户端预校验、调用任务接口。
 * 对齐 iOS TaskService。页面应调用本文件。
 */
import emitter from '@/assets/js/eventbus'
import { ENABLE_REWARD_TASKS } from '@/assets/js/featureFlags'
import { trackRewardTaskComplete } from '@/assets/js/rewardAnalytics'
import http from './http'
import {
  localCalendarDayString
} from './rewardMock'
import {
  TASK_STATUS,
  REWARD_ERROR_CODE,
  RewardError,
  REWARD_TASK_LIST_CHANGE_EVENT
} from './rewardConstants'

export {
  TASK_TYPE,
  TASK_STATUS,
  REWARD_ERROR_CODE,
  RewardError,
  getRewardErrorI18nKey,
  sortRewardTasksByStatus,
  REWARD_TASK_LIST_CHANGE_EVENT
} from './rewardConstants'

let cachedTasks = null
let cachedDay = ''

function cloneTask (task) {
  return task ? { ...task } : null
}

function cloneTaskList (list) {
  return (Array.isArray(list) ? list : []).map(cloneTask)
}

function normalizeTaskList (data) {
  return (Array.isArray(data) ? data : [])
    .map((task, index) => ({
      task_id: task?.task_name || '',
      task_type: task?.task_type || '',
      status: Number(task?.status) || TASK_STATUS.UNFINISHED,
      amount: Number(task?.amount) || 0,
      progress: Number(task?.progress) || 0,
      total: Number(task?.total) > 0 ? Number(task.total) : 1,
      jsonIndex: index
    }))
    .filter(task => task.task_id)
}

function hasFreshCacheForToday () {
  return (
    Array.isArray(cachedTasks) &&
    cachedTasks.length > 0 &&
    cachedDay === localCalendarDayString()
  )
}

function snapshotTasks () {
  return cloneTaskList(cachedTasks)
}

function replaceCacheWithTasks (tasks) {
  cachedTasks = cloneTaskList(tasks)
  cachedDay = localCalendarDayString()
}

function cachedTaskWithId (taskId) {
  return cachedTasks?.find((task) => task?.task_id === taskId) || null
}

function updateCacheWithTask (task) {
  if (!task?.task_id || !cachedTasks) return

  const idx = cachedTasks.findIndex((item) => item?.task_id === task.task_id)
  if (idx >= 0) {
    cachedTasks[idx] = {
      ...cachedTasks[idx],
      status: task.status,
      progress: task.progress
    }
  } else {
    cachedTasks.push(cloneTask(task))
  }
}

function emitRewardTaskListChange () {
  emitter.emit(REWARD_TASK_LIST_CHANGE_EVENT)
}

function didAdvanceTask (before, updated) {
  if (!updated) return false
  if (!before || before.status === TASK_STATUS.UNFINISHED) {
    const prevProgress = Number(before?.progress || 0)
    return (
      Number(updated.progress) > prevProgress ||
      updated.status === TASK_STATUS.CLAIMABLE
    )
  }
  return false
}

export function clearRewardTasksCache () {
  cachedTasks = null
  cachedDay = ''
}

export async function getRewardTasks (_uid) {
  if (!ENABLE_REWARD_TASKS) {
    return { tasks: [] }
  }

  if (hasFreshCacheForToday()) {
    return { tasks: snapshotTasks() }
  }

  const data = await http.get('/api/task/info')
  replaceCacheWithTasks(normalizeTaskList(data))
  return { tasks: snapshotTasks() }
}

export async function executeTask (_uid, taskId, progressDelta = 1, position) {
  if (!ENABLE_REWARD_TASKS) {
    return null
  }

  if (!taskId) {
    throw new RewardError(REWARD_ERROR_CODE.TASK_NOT_FOUND, 'Task not found')
  }

  const before = cachedTaskWithId(taskId)
  if (
    hasFreshCacheForToday() &&
    before &&
    before.status !== TASK_STATUS.UNFINISHED
  ) {
    return null
  }

  const data = await http.post('/api/task/progress', {
    taskName: taskId,
    progressDelta: Number(progressDelta) > 0 ? Number(progressDelta) : 1
  })
  const updated = normalizeTaskList([data])[0] || null
  if (updated) {
    updateCacheWithTask(updated)
    emitRewardTaskListChange()
  }

  if (!didAdvanceTask(before, updated)) {
    return null
  }

  if (updated.status === TASK_STATUS.CLAIMABLE) {
    trackRewardTaskComplete({ taskId, position })
  }

  return cloneTask(updated)
}

export async function executeTaskAndReturnClaimable (
  _uid,
  taskId,
  progressDelta = 1,
  position
) {
  const task = await executeTask(_uid, taskId, progressDelta, position)
  if (!task || task.status !== TASK_STATUS.CLAIMABLE) {
    return null
  }
  return task
}

export async function claimRewardTask (_uid, taskId) {
  if (!ENABLE_REWARD_TASKS) {
    throw new RewardError(REWARD_ERROR_CODE.TASK_NOT_FOUND, 'Task not found')
  }

  if (!taskId) {
    throw new RewardError(REWARD_ERROR_CODE.TASK_NOT_FOUND, 'Task not found')
  }

  const cached = cachedTaskWithId(taskId)
  if (hasFreshCacheForToday() && cached) {
    if (cached.status === TASK_STATUS.UNFINISHED) {
      throw new RewardError(
        REWARD_ERROR_CODE.TASK_NOT_COMPLETED,
        'Task not completed'
      )
    }
    if (cached.status === TASK_STATUS.CLAIMED) {
      throw new RewardError(
        REWARD_ERROR_CODE.TASK_REWARD_ALREADY_CLAIMED,
        'Reward already claimed'
      )
    }
  }

  const data = await http.post('/api/task/claim', {
    taskName: taskId
  })
  const updated = normalizeTaskList([data])[0] || null
  if (updated) {
    updateCacheWithTask(updated)
    emitRewardTaskListChange()
  }
  return cloneTask(updated)
}
